import { useEffect, useState } from 'react';
import { materialUnitApi } from '../../api';
import { useAuth } from '../../context/AuthContext';

// Per-unit Material Information values — meant to be embedded inside an
// existing "Material ID history" modal (WorkerList.jsx, InventoryPage.jsx,
// SiteHistory.jsx, AssetHistory.jsx, MyProfile.jsx all already open one when
// a Material ID is clicked). Renders nothing if the unit's material type has
// no custom fields defined (see MaterialInfoFieldsModal, managed in Master
// Data) — keeps the history modal unchanged for every material that doesn't
// use this feature.
// `unitKitId`/`unitItItemId` are the unit's kitId/itItemId if it's a
// material/IT item, or both falsy if it's a stationery item (i.e. pass
// historyModal.unit?.kitId and historyModal.unit?.itItemId) — used only to
// pick which of the three split permissions (materials_info_manage vs
// stationery_info_manage vs it_info_manage) gates the Edit controls here.
export default function MaterialInfoPanel({ materialUnitId, unitKitId, unitItItemId }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(unitKitId ? 'materials_info_manage' : unitItItemId ? 'it_info_manage' : 'stationery_info_manage');

  const [rows, setRows] = useState(null); // null = loading
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!materialUnitId) return;
    setRows(null);
    setEditingFieldId(null);
    setError('');
    materialUnitApi.getInfo(materialUnitId)
      .then((res) => setRows(res.data.data))
      .catch(() => setRows([]));
  }, [materialUnitId]);

  if (!materialUnitId || rows === null || rows.length === 0) return null;

  const startEdit = (row) => {
    setEditingFieldId(row.fieldId);
    setEditingValue(row.value || '');
  };

  const save = async (fieldId) => {
    setSaving(true);
    setError('');
    try {
      await materialUnitApi.updateInfo(materialUnitId, { fieldId, value: editingValue });
      setRows((prev) => prev.map((r) => (r.fieldId === fieldId ? { ...r, value: editingValue || null } : r)));
      setEditingFieldId(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save value');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Material Information</p>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.fieldId} className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 w-1/3 flex-shrink-0 truncate">{r.key}</span>
            {editingFieldId === r.fieldId ? (
              <>
                <input
                  className="input flex-1 py-1"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  autoFocus
                />
                <button className="text-blue-600 hover:underline text-xs" disabled={saving} onClick={() => save(r.fieldId)}>Save</button>
                <button className="text-gray-500 hover:underline text-xs" onClick={() => setEditingFieldId(null)}>Cancel</button>
              </>
            ) : (
              <>
                <span className="flex-1 text-gray-800">{r.value || <span className="text-gray-300">—</span>}</span>
                {canManage && (
                  <button className="text-blue-600 hover:underline text-xs" onClick={() => startEdit(r)}>Edit</button>
                )}
              </>
            )}
          </div>
        ))}
      </div>
      {error && <p className="text-red-600 text-xs mt-2">{error}</p>}
    </div>
  );
}
